import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { notification } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import useIsMobile from '@shared/hooks/useIsMobile';

import Utils from '@/Utils';

import css from './Notifications.module.scss';

let audioNotification;
let isStartAudio = false;
const NOTIFICATION_DURATION = 5;

// Глобальная переменная для API уведомлений
let globalNotificationAPI = null;

// Глобальная функция для показа уведомлений
export const showNotification = ({
  type = 'info',
  message,
  description,
  duration = NOTIFICATION_DURATION,
  placement,
  ...options
}) => {
  if (!globalNotificationAPI) {
    console.warn('Notification API not initialized yet');
    return;
  }

  const config = {
    message,
    description,
    duration,
    placement: placement || 'bottomRight',
    className: css['notifications--box'],
    ...options,
  };

  switch (type) {
    case 'success':
      globalNotificationAPI.success(config);
      break;
    case 'error':
      globalNotificationAPI.error(config);
      break;
    case 'warning':
      globalNotificationAPI.warning(config);
      break;
    case 'info':
      globalNotificationAPI.info(config);
      break;
    default:
      globalNotificationAPI.open(config);
  }
};

const Notifications = ({
  // Пропсы для user части (WebSocket уведомления)
  enableWebSocket = false,
  clearUnReadNotifications = null,
  setUnReadNotifications = null,
  useNotificationTypes = null,
  ConnectStompClient = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('myNotification');
  const [isMobile] = useIsMobile(992);
  // const [isXSMobile] = useIsMobile(375);
  const navigate = useNavigate();

  const notificationConfig = { top: isMobile ? '20vh' : '24px' };
  const [api, contextHolder] = notification.useNotification(notificationConfig);

  // Инициализируем глобальный API
  useEffect(() => {
    globalNotificationAPI = api;
    return () => {
      globalNotificationAPI = null;
    };
  }, [api]);

  const newNotificantion = useCallback(
    msg => {
      if (!enableWebSocket || !useNotificationTypes) return;

      if (isStartAudio && audioNotification) {
        audioNotification.volume = 0;
      }

      const { IconNotification } = useNotificationTypes({ type: msg.groupTemplateType, t });

      const key = `notification-key-${msg.id}`;
      const message = isMobile ? Utils.trString(msg.subject, 32) : Utils.trString(msg.subject, 47);
      const description = isMobile ? Utils.trString(msg.content, 32) : Utils.trString(msg.content, 57);

      const CloseIconMobile = () => (
        <span className={css['notifications--close']}>
          <CloseOutlined />
        </span>
      );

      api.open({
        message: (
          <Link className={css['notifications--message']} to={`/my-notifications?modal_id=${msg.id}`}>
            {message}
          </Link>
        ),
        placement: isMobile ? 'top' : 'bottomRight',
        description: (
          <Link className={css['notifications--description']} to={`/my-notifications?modal_id=${msg.id}`}>
            {description}
          </Link>
        ),
        className: css['notifications--box'],
        key,
        icon: !isMobile ? <IconNotification /> : null,
        closeIcon: isMobile ? <CloseIconMobile /> : undefined,
      });
    },
    [api, isMobile, t, navigate, enableWebSocket, useNotificationTypes]
  );

  const eventOnceInitAudio = () => {
    isStartAudio = true;
    window.removeEventListener('click', eventOnceInitAudio);
  };

  useEffect(() => {
    if (!enableWebSocket) return;

    let wasOffline = false;

    const JWT_TOKEN = localStorage.getItem('authorizationToken') || '';
    const SPLIT_JWT_TOKEN = JWT_TOKEN.split(' ');

    const stompClient = new ConnectStompClient(
      SPLIT_JWT_TOKEN[1],
      3,
      5000,
      newNotificantion,
      (listNotification, isIncrement) => {
        dispatch(setUnReadNotifications(listNotification, isIncrement));
      }
    );

    audioNotification = new Audio('/assets/sounds/notification.wav');
    audioNotification.volume = 0;

    const windowListener = window.addEventListener('click', eventOnceInitAudio);

    const openNotification = ({ key, message, description, duration = NOTIFICATION_DURATION, onClick }) => {
      api.open({
        message,
        description,
        placement: isMobile ? 'top' : 'Right',
        className: css['notifications--box'],
        key,
        duration,
        onClick,
      });
    };

    //  Оффлайн уведомление
    const handleOfflineNotification = () => {
      wasOffline = true;
      const key = 'offline-mode-warning';

      openNotification({
        key,
        message: <div className={css['notifications--message']}>{t('notificationsType.14')}</div>,
        description: <div className={css['notifications--description']}>{t('offlineNotification')}</div>,
        onClick: () => {
          navigate('/offline-materials');
          api.destroy(key);
        },
      });
    };

    const handleOnlineNotification = () => {
      if (!wasOffline) return;

      openNotification({
        key: 'online-mode-info',
        message: <div className={css['notifications--message']}>{t('notificationsType.15')}</div>,
        description: <div className={css['notifications--description']}>{t('onlineNotification')}</div>,
      });

      wasOffline = false;
    };

    if (!Utils.isOnline()) handleOfflineNotification();

    window.addEventListener('offline', handleOfflineNotification);
    window.addEventListener('online', handleOnlineNotification);

    return () => {
      dispatch(clearUnReadNotifications());
      stompClient.disconnect();
      if (windowListener) window.removeEventListener('click', eventOnceInitAudio);
      window.removeEventListener('offline', handleOfflineNotification);
      window.removeEventListener('online', handleOnlineNotification);
    };
  }, [
    enableWebSocket,
    api,
    isMobile,
    t,
    navigate,
    dispatch,
    clearUnReadNotifications,
    setUnReadNotifications,
    newNotificantion,
    ConnectStompClient,
  ]);

  return <div className={css.DragItem}>{contextHolder}</div>;
};

export default Notifications;
